import React from 'react';
import { formatCurrency } from 'utils';
import { Typography } from '@mui/material';
import { FormInput } from 'components/form';
import { green, grey, lightGreen } from '@mui/material/colors';
import { Column } from 'components/shared/TableListing/types';
import { Money, PriceCheck, ArrowUpward, AttachMoney, AccountBalance, CheckCircleOutlined } from '@mui/icons-material';

import { calculateTotal, chargeCalc } from '../../lib';
import { Charge, ChargeListingContext } from '../../types';
import { ChargeTotalCell, ChargeValueCell, ChargeTypeCell, ChargeValueWithTypesCell } from './cells';

export const chargesListingColumns: Column<Charge, ChargeListingContext>[] = [
  {
    header: 'Equipment Charges',
    renderKey: 'type',
    renderCell: ({ context: { onRemoveRow }, rowIndex }) => (
      <ChargeTypeCell
        rowIndex={rowIndex}
        onRemoveRow={() => {
          onRemoveRow(rowIndex);
        }}
      />
    ),
    renderFooter: () => <Typography>Total</Typography>,
    headerStyles: {
      minHeight: 'auto',
      backgroundColor: grey[200],
    },
    cellStyles: {
      minWidth: '10rem',
      alignItems: 'flex-start',
    },
    footerStyles: {
      minHeight: 'auto',
      color: green[800],
      backgroundColor: lightGreen['50'],
    },
  },
  {
    renderKey: 'wholesalePrice',
    header: 'Wholesale Price',
    startIcon: <PriceCheck fontSize="small" />,
    renderCell: ({ rowIndex }) => <ChargeValueCell index={rowIndex} type="wholesalePrice" />,
    renderFooter: ({ context: { form } }) => <ChargeTotalCell form={form} field="wholesalePrice" />,
    headerStyles: {
      minHeight: 'auto',
      backgroundColor: grey[200],
    },
    cellStyles: {
      minWidth: '10rem',
      alignItems: 'flex-start',
    },
    footerStyles: {
      minHeight: 'auto',
      color: green[800],
      backgroundColor: lightGreen['50'],
    },
  },
  {
    header: 'Markup',
    renderKey: 'markup',
    startIcon: <ArrowUpward fontSize="small" />,
    renderCell: ({ rowIndex }) => <ChargeValueWithTypesCell index={rowIndex} type="markup" />,
    renderFooter: ({ context: { form } }) => <ChargeTotalCell form={form} field="markup" />,
    headerStyles: {
      minHeight: 'auto',
      backgroundColor: grey[200],
    },
    cellStyles: {
      minWidth: '10rem',
      alignItems: 'flex-start',
    },
    footerStyles: {
      minHeight: 'auto',
      color: green[800],
      backgroundColor: lightGreen['50'],
    },
  },
  {
    header: 'Retail Price',
    renderKey: 'retailPrice',
    startIcon: <AttachMoney fontSize="small" />,
    renderCell: ({ context: { form }, rowIndex }) => {
      const charge = form.watch(`bodyFields.${rowIndex}`);
      const retailPrice = chargeCalc.calcRetailPrice(charge);

      return <FormInput disabled variant="outlined" value={formatCurrency(retailPrice, true)} />;
    },
    renderFooter: ({ context: { form } }) => <ChargeTotalCell form={form} field="retailPrice" />,
    headerStyles: {
      minHeight: 'auto',
      backgroundColor: grey[200],
    },
    cellStyles: {
      minWidth: '10rem',
      alignItems: 'flex-start',
    },
    footerStyles: {
      minHeight: 'auto',
      color: green[800],
      backgroundColor: lightGreen['50'],
    },
  },
  {
    header: 'Net Price',
    renderKey: 'netPrice',
    startIcon: <Money fontSize="small" />,
    renderCell: ({ context: { form }, rowIndex }) => {
      const charge = form.watch(`bodyFields.${rowIndex}`);
      const netPrice = chargeCalc.calcNetPrice(charge);

      return <FormInput disabled variant="outlined" value={formatCurrency(netPrice, true)} />;
    },
    renderFooter: ({ context: { form } }) => <ChargeTotalCell form={form} field="netPrice" />,
    headerStyles: {
      minHeight: 'auto',
      backgroundColor: grey[200],
    },
    cellStyles: {
      minWidth: '10rem',
      alignItems: 'flex-start',
    },
    footerStyles: {
      minHeight: 'auto',
      color: green[800],
      backgroundColor: lightGreen['50'],
    },
  },
  {
    header: 'Sales Tax',
    renderKey: 'salesTax',
    startIcon: <AccountBalance fontSize="small" />,
    renderCell: ({ rowIndex }) => <ChargeValueWithTypesCell index={rowIndex} type="salesTax" />,
    renderFooter: ({ context: { form } }) => <ChargeTotalCell form={form} field="salesTax" />,
    headerStyles: {
      minHeight: 'auto',
      backgroundColor: grey[200],
    },
    cellStyles: {
      minWidth: '10rem',
      alignItems: 'flex-start',
    },
    footerStyles: {
      minHeight: 'auto',
      color: green[800],
      backgroundColor: lightGreen['50'],
    },
  },
  {
    header: 'Total',
    renderKey: 'total',
    startIcon: <CheckCircleOutlined fontSize="small" />,
    renderCell: ({ context: { form }, rowIndex }) => {
      const charge = form.watch(`bodyFields.${rowIndex}`);
      const total = chargeCalc.calcChargeTotal(charge);

      return <Typography>{formatCurrency(total, true)}</Typography>;
    },
    renderFooter: ({ context: { form } }) => {
      const value = form
        .watch(`bodyFields`)
        .reduce<number>((total, charge) => (total += calculateTotal(charge.price)), 0);

      return <Typography>{formatCurrency(value, true)}</Typography>;
    },
    headerStyles: {
      minHeight: 'auto',
      backgroundColor: grey[200],
    },
    cellStyles: {
      minWidth: '10rem',
      alignItems: 'flex-start',
    },
    footerStyles: {
      minHeight: 'auto',
      color: green[800],
      backgroundColor: lightGreen['50'],
    },
  },
];
